import { Navigate } from "react-router-dom";
import { DialogsProvider } from "@/context/DialogsContext";
import SEOPageLayout from "@/components/seo/SEOPageLayout";
import QuoteFormDialog from "@/components/forms/QuoteFormDialog";
import { useSEO } from "@/hooks/useSEO";
import { resolvePlace } from "@/data/seo/cities";
import { getRouteBySlug, getPhase1Routes } from "@/data/seo/routes";
import { getPhase1MainServices } from "@/data/seo/mainServices";
import { WHY_US_ITEMS, FAQ_ITEMS } from "@/constants/business";
import { localBusinessSchema, serviceSchema, faqSchema, breadcrumbSchema } from "@/lib/schema";

export default function RoutePage({ routeSlug }) {
  const route = getRouteBySlug(routeSlug);
  const fromPlace = route ? resolvePlace(route.fromSlug) : null;
  const toPlace = route ? resolvePlace(route.toSlug) : null;
  const isValid = !!(route && fromPlace && toPlace);

  const path = `/transport-${routeSlug}`;
  const title = isValid ? `Transport ${fromPlace.name} ↔ ${toPlace.name} — Express Delivery` : "";
  const description = isValid
    ? `Express Delivery organise vos transports entre ${fromPlace.name} et ${toPlace.name} : meubles, électroménager, déménagements, groupage possible selon disponibilité.`
    : "";
  const faqSubset = isValid
    ? [
        { q: "Le groupage est-il possible sur ce trajet ?", a: `Selon les tournées disponibles, un groupage entre ${fromPlace.name} et ${toPlace.name} peut permettre d'optimiser le tarif.` },
        ...FAQ_ITEMS.slice(0, 4),
      ]
    : [];
  const otherRoutes = isValid ? getPhase1Routes().filter((r) => r.slug !== route.slug) : [];

  const breadcrumbItems = isValid ? [{ label: "Accueil", href: "/" }, { label: `${fromPlace.name} → ${toPlace.name}` }] : [];

  useSEO({
    title,
    description,
    path,
    isIndexable: !!route?.isPhase1,
    jsonLd: isValid
      ? [
          localBusinessSchema(),
          serviceSchema({ name: `Transport ${fromPlace.name} ↔ ${toPlace.name}`, description, url: path }),
          faqSchema(faqSubset),
          breadcrumbSchema(breadcrumbItems),
        ]
      : null,
  });

  if (!isValid) return <Navigate to="/404" replace />;

  const relatedGroups = [
    {
      title: "Villes concernées",
      items: [fromPlace, toPlace]
        .filter((p) => p.isPhase1)
        .map((p) => ({ label: p.name, href: `/transport-${p.slug}` })),
    },
    {
      title: "Autres trajets",
      items: otherRoutes.map((r) => {
        const f = resolvePlace(r.fromSlug);
        const t = resolvePlace(r.toSlug);
        return { label: `${f?.name || r.fromSlug} → ${t?.name || r.toSlug}`, href: `/transport-${r.slug}` };
      }),
    },
  ];

  return (
    <DialogsProvider>
      <SEOPageLayout
        testIdPrefix="route-page"
        breadcrumbItems={breadcrumbItems}
        eyebrow="Trajet & longue distance"
        h1={`Transport ${fromPlace.name} ↔ ${toPlace.name}`}
        intro={`Express Delivery organise des transports entre ${fromPlace.name} et ${toPlace.name} : meubles, électroménager, cartons, achats entre particuliers et déménagements. Le trajet est optimisé à l'aller comme au retour lorsque le groupage est possible.`}
        serviceLinks={getPhase1MainServices().map((s) => ({ label: s.label, href: `/${s.slug}` }))}
        trustItems={WHY_US_ITEMS.slice(2, 8).map((i) => i.text)}
        relatedGroups={relatedGroups}
        faqItems={faqSubset}
        ctaProfile="particulier"
      />
      <QuoteFormDialog />
    </DialogsProvider>
  );
}
